import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Notify } from 'notiflix';
import {
  Box,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Button,
  Chip,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Tab,
  Tabs,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Star as StarIcon,
  Category as CategoryIcon,
  Tag as TagIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { selectCurrentValuePack } from 'store/slices/valuePackSlice';
import { selectAllCategoriesWithFeatures } from 'store/slices/valuePackSlice';
import { selectLoading } from 'store/slices/valuePackSlice';
import { selectError } from 'store/slices/valuePackSlice';
import { fetchValuePackDetails } from 'store/slices/valuePackSlice';
import { fetchCategoriesOptimized } from 'store/slices/valuePackSlice';
import { formatDate } from 'utils/valuePack/formatters';
import { clearError } from 'store/slices/valuePackSlice';
import { getCountryByCode } from 'utils/valuePack/countryUtils';
import { formatCurrency } from 'utils/valuePack/formatters';
import { getCategoriesByIds, getDefaultTierFilter } from 'utils/valuePack/valuePackUtils';
import usePermission from 'hooks/usePermission';

const ValuePackView = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const canUpdate = usePermission('shop-management', 'update');

  const currentValuePack = useSelector(selectCurrentValuePack);
  const allCategories = useSelector(selectAllCategoriesWithFeatures);
  
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  
  // Only trust the stored pack when it is the one in the URL (the store keeps
  // the last opened pack around between screens).
  const valuePack =
    currentValuePack && String(currentValuePack.id) === String(id) ? currentValuePack : null;
  const [loadFailed, setLoadFailed] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [filterDuration, setFilterDuration] = useState('all');
  const [filterCountry, setFilterCountry] = useState('all');
  const defaultFiltersApplied = useRef(false);

  useEffect(() => {
    if (defaultFiltersApplied.current) return;
    if (!valuePack?.tiers?.length) return;

    defaultFiltersApplied.current = true;
    const defaults = getDefaultTierFilter(valuePack.tiers);
    setFilterCountry(defaults.country);
    setFilterDuration(defaults.duration);
  }, [valuePack]);

  const availableDurations = useMemo(() => {
    const durations = new Set();
    (valuePack?.tiers || []).forEach((tier) => {
      if (tier.duration !== undefined && tier.duration !== null) {
        durations.add(tier.duration);
      }
    });
    return Array.from(durations).sort((a, b) => a - b);
  }, [valuePack]);

  const availableCountries = useMemo(() => {
    const countries = new Map();
    (valuePack?.tiers || []).forEach((tier) => {
      if (tier.countryCode && !countries.has(tier.countryCode)) {
        countries.set(tier.countryCode, getCountryByCode(tier.countryCode));
      }
    });
    return Array.from(countries.entries());
  }, [valuePack]);

  const filteredTiers = useMemo(() => {
    return (valuePack?.tiers || []).filter((tier) => {
      const matchesDuration =
        filterDuration === 'all' || String(tier.duration) === String(filterDuration);
      const matchesCountry =
        filterCountry === 'all' || tier.countryCode === filterCountry;
      return matchesDuration && matchesCountry;
    });
  }, [valuePack, filterDuration, filterCountry]);

  // The pack itself is fetched on demand (list rows only carry minimal data).
  const loadValuePack = useCallback(async () => {
    setLoadFailed(false);
    try {
      await dispatch(fetchValuePackDetails(id)).unwrap();
    } catch (err) {
      setLoadFailed(true);
      Notify.failure(typeof err === 'string' ? err : 'Failed to load value pack');
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (!id) {
      Notify.failure('Invalid value pack ID');
      navigate('/value-pack');
      return;
    }
    loadValuePack();
  }, [id, navigate, loadValuePack]);

  // Category catalog (with features) backs the Features tab.
  useEffect(() => {
    if (!allCategories.length) {
      dispatch(fetchCategoriesOptimized()).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    return () => {
      if (error) {
        dispatch(clearError());
      }
    };
  }, [dispatch, error]);

  const categories = useMemo(() => {
    // valuePack.categories only carries id/name/description (no features);
    // cross-reference allCategories (loaded with features) by id to get the
    // full category objects for the Features tab.
    const categoryIds = (valuePack?.categories || []).map((cat) =>
      cat && typeof cat === 'object' ? cat.id : cat
    );
    return getCategoriesByIds(categoryIds, allCategories);
  }, [valuePack, allCategories]);

  const handleRetry = () => {
    dispatch(clearError());
    loadValuePack();
  };

  if (loading && !valuePack) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box pt={6} pb={3} display="flex" justifyContent="center">
          <Typography>Loading...</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box pt={6} pb={3} display="flex" flexDirection="column" alignItems="center">
          <Typography color="error" gutterBottom>Error: {error}</Typography>
          <Button
            variant="contained"
            onClick={handleRetry}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Retry'}
          </Button>
        </Box>
      </DashboardLayout>
    );
  }

  if (!loading && loadFailed && !valuePack) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box pt={6} pb={3} display="flex" flexDirection="column" alignItems="center">
          <Typography gutterBottom>Value pack not found</Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/value-pack')}
          >
            Back to Value Packs
          </Button>
        </Box>
      </DashboardLayout>
    );
  }

  if (!valuePack) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box pt={6} pb={3} display="flex" justifyContent="center">
          <Typography>Loading value pack...</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  const renderBasicInfo = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Box>
                <Typography variant="h4" gutterBottom>
                  {valuePack.name}
                  {valuePack.featured && (
                    <Chip 
                      icon={<StarIcon />}
                      label="Featured" 
                      color="secondary" 
                      sx={{ ml: 2 }} 
                    />
                  )}
                </Typography>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  {valuePack.shortDescription}
                </Typography>
              </Box>
              <Chip
                label={valuePack.active ? 'Active' : 'Inactive'}
                color={valuePack.active ? 'success' : 'error'}
                variant="outlined"
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Description
            </Typography>
            <Typography variant="body1" paragraph>
              {valuePack.description}
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Categories
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {categories.map(cat => {
              // console.log("cat : ", cat);
              return (
                <Chip
                  key={cat.id}
                  icon={<CategoryIcon />}
                  label={cat.name}
                  color="primary"
                  variant="outlined"
                />
              )})}
            </Box>

            <Typography variant="h6" gutterBottom>
              Tags
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {valuePack.tags?.map(tag => (
                <Chip
                  key={tag}
                  icon={<TagIcon />}
                  label={tag}
                  variant="outlined"
                />
              ))}
            </Box>

            <Typography variant="h6" gutterBottom>
              Target Industries
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {valuePack.targetIndustries?.map(industry => (
                <Chip
                  key={industry}
                  icon={<BusinessIcon />}
                  label={industry}
                  color="info"
                  variant="outlined"
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={4}>
        {valuePack.imageUrl && (
          <Card sx={{ mb: 3 }}>
            <CardHeader title="Main Image" />
            <CardContent>
              <img
                src={valuePack.imageUrl}
                alt={valuePack.name}
                style={{
                  width: '100%',
                  height: '200px',
                  objectFit: 'cover',
                  borderRadius: '8px'
                }}
              />
            </CardContent>
          </Card>
        )}

        {valuePack.bannerImageUrl && (
          <Card sx={{ mb: 3 }}>
            <CardHeader title="Banner Image" />
            <CardContent>
              <img
                src={valuePack.bannerImageUrl}
                alt={`${valuePack.name} Banner`}
                style={{
                  width: '100%',
                  height: '150px',
                  objectFit: 'cover',
                  borderRadius: '8px'
                }}
              />
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader title="Metadata" />
          <CardContent>
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                Created
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(valuePack.createdAt)}
              </Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                Last Updated
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(valuePack.updatedAt)}
              </Typography>
            </Box>

            {valuePack.publishedAt && (
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">
                  Published At
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatDate(valuePack.publishedAt)}
                  </Typography>
              </Box>
            )}

            {valuePack.expiresAt && (
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Expires At
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatDate(valuePack.expiresAt)}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderFeatures = () => (
    <Grid container spacing={3}>
      {/* Included Features */}
      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader title="Included Features" />
          <CardContent>
            {categories?.map((category) => {
              const features = category.features || [];

              return features.length > 0 ? (
                <Box key={category.id} mb={3}>
                  <Typography variant="subtitle1" gutterBottom>
                    {category.name}
                  </Typography>
                  <List dense>
                    {features.map((feature, index) => (
                      <ListItem key={feature.id || index}>
                        <ListItemIcon>
                          <CheckIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary={feature.name} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ) : (
                <Box key={category.id} mb={3}>
                  <Typography variant="subtitle1" gutterBottom>
                    {category.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    fontStyle="italic"
                  >
                    No included features available
                  </Typography>
                </Box>
              );
            })}
          </CardContent>
        </Card>
      </Grid>

      {/* Additional Features */}
      {/* <Grid item xs={12} md={6}>
        <Card>
          <CardHeader title="Additional Features" />
          <CardContent>
            {categories?.map((category) => {
              const additionalFeatures = category.additionalFeatures || [];

              return additionalFeatures.length > 0 ? (
                <Box key={category.id} mb={3}>
                  <Typography variant="subtitle1" gutterBottom>
                    {category.name}
                  </Typography>
                  <List dense>
                    {additionalFeatures.map((feature, index) => (
                      <ListItem key={feature.id || index}>
                        <ListItemIcon>
                          <StarIcon color="warning" />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature.name}
                          secondary="Premium feature"
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ) : (
                <Box key={category.id} mb={3}>
                  <Typography variant="subtitle1" gutterBottom>
                    {category.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    fontStyle="italic"
                  >
                    No additional features available
                  </Typography>
                </Box>
              );
            })}
          </CardContent>
        </Card>
      </Grid> */}
    </Grid>
  );

  const renderPricingTiers = () => (
    <Card
      sx={{
        boxShadow: "none",
        border: "1px solid rgba(224, 224, 224, 1)",
        overflow: "hidden",
      }}
    >
      <CardHeader
        title="Pricing Tiers"
        action={
          canUpdate && (
            <Button
              variant="outlined"
              sx={{
                color: "#39a7f1",
                borderColor: "#39a7f1",
                "&:hover": {
                  borderColor: "#39a7f1",
                  backgroundColor: "rgba(57, 167, 241, 0.04)",
                },
              }}
              onClick={() => navigate(`/value-pack/${id}/tiers`)}
            >
              Manage Tiers
            </Button>
          )
        }
      />
      <CardContent sx={{ p: 0 }}>
        {valuePack?.tiers?.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              px: 3,
              py: 2,
              borderBottom: "1px solid rgba(224, 224, 224, 1)",
            }}
          >
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Duration</InputLabel>
              <Select
                label="Duration"
                value={filterDuration}
                onChange={(e) => setFilterDuration(e.target.value)}
              >
                <MenuItem value="all">All Durations</MenuItem>
                {availableDurations.map((duration) => (
                  <MenuItem key={duration} value={duration}>
                    {duration} Month{duration > 1 ? "s" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Country</InputLabel>
              <Select
                label="Country"
                value={filterCountry}
                onChange={(e) => setFilterCountry(e.target.value)}
              >
                <MenuItem value="all">All Countries</MenuItem>
                {availableCountries.map(([code, countryData]) => (
                  <MenuItem key={code} value={code}>
                    {countryData?.name || code} ({code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {filteredTiers.length > 0 ? (
          <Box>
            {/* Header Row */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                backgroundColor: "#f5f5f5",
                px: 3,
                py: 2,
                borderBottom: "1px solid rgba(224, 224, 224, 1)",
              }}
            >
              <Box sx={{ width: "16%", pr: 2 }}>
                <Typography fontWeight="bold">Tier</Typography>
              </Box>
              <Box sx={{ width: "16%", pr: 2 }}>
                <Typography fontWeight="bold">Description</Typography>
              </Box>
              <Box sx={{ width: "16%", pr: 2 }}>
                <Typography fontWeight="bold">Pricing</Typography>
              </Box>
              <Box sx={{ width: "16%", pr: 2 }}>
                <Typography fontWeight="bold">Duration</Typography>
              </Box>
              <Box sx={{ width: "16%", pr: 2 }}>
                <Typography fontWeight="bold">Country</Typography>
              </Box>
              <Box sx={{ width: "20%" }}>
                <Typography fontWeight="bold">Features</Typography>
              </Box>
            </Box>

            {/* Tiers List */}
            {filteredTiers.map((tier, index) => {
              // find all features of this tier
              const tierFeatures = tier.featuresConfiguration?.map((f) => {
                const category = allCategories?.find(
                  (cat) => String(cat.id) === String(f.categoryId)
                );
                const feature =
                  category?.features?.find(
                    (feat) => String(feat.id) === String(f.featureId)
                  ) || null;
                return feature ? feature.name : null;
              }).filter(Boolean);

              return (
                <Box
                  key={tier.id}
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    p: { xs: 2, md: 3 },
                    borderBottom:
                      index === filteredTiers.length - 1
                        ? "none"
                        : "1px solid rgba(224, 224, 224, 1)",
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                  }}
                >
                  {/* Tier Column */}
                  <Box sx={{ width: { md: "16%" }, pr: { md: 2 } }}>
                    <Typography fontWeight="medium">{tier.tierType}</Typography>
                    {tier.isPopular && (
                      <Chip
                        label="Popular"
                        color="secondary"
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    )}
                    {tier.name && (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ mt: 1 }}
                      >
                        {tier.name}
                      </Typography>
                    )}
                  </Box>

                  {/* Description Column */}
                  <Box sx={{ width: { md: "16%" }, pr: { md: 2 } }}>
                    <Typography variant="body2">
                      {tier.description || "No description"}
                    </Typography>
                  </Box>

                  {/* Pricing Column */}
                  {/* <Box sx={{ width: { md: "20%" }, pr: { md: 2 } }}>
                    <Typography fontWeight="medium">
                      {formatCurrency(tier.currentPrice, "INR")}
                    </Typography>
                    {tier.discountValue > 0 && (
                      <Box display="flex" alignItems="center" mt={0.5}>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ textDecoration: "line-through", mr: 1 }}
                        >
                          {formatCurrency(tier.originalPrice, "INR")}
                        </Typography>
                        <Chip
                          label={`Save ${formatCurrency(
                            tier.discountValue,
                            "INR"
                          )}`}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      </Box>
                    )}
                  </Box> */}

                  <Box sx={{ width: { md: "16%" }, pr: { md: 2 } }}>
                    <Typography fontWeight="medium">
                      {formatCurrency(tier.currentPrice, "INR")}
                    </Typography>

                    {tier.discountValue > 0 && (
                      <Box display="flex" alignItems="center" mt={0.5}>
                        {/* Original Price (cut-through) */}
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ textDecoration: "line-through", mr: 1 }}
                        >
                          {formatCurrency(tier.originalPrice, "INR")}
                        </Typography>

                        {/* Discount Label */}
                        <Chip
                          label={
                            tier.discountType === "percentage"
                              ? `Save ${tier.discountValue}%`
                              : `Save ${formatCurrency(tier.discountValue, "INR")}`
                          }
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      </Box>
                    )}
                  </Box>

                  {/* Duration */}
                  <Box sx={{ width: { md: "16%" }, pr: { md: 2 } }}>
                    <Chip
                      label={`${tier.duration} month${
                        tier.duration > 1 ? "s" : ""
                      }`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>

                  {/* Country */}
                  <Box sx={{ width: { md: "16%" }, pr: { md: 2 } }}>
                    <Chip
                      label={
                        getCountryByCode(tier.countryCode)?.name ||
                        tier.countryCode ||
                        "-"
                      }
                      size="small"
                      variant="outlined"
                    />
                  </Box>

                  {/* Features */}
                  <Box sx={{ width: { md: "20%" } }}>
                    {tierFeatures?.length > 0 ? (
                      <List dense>
                        {tierFeatures.map((fname, idx) => (
                          <ListItem key={idx} sx={{ py: 0 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <CheckIcon color="success" fontSize="small" />
                            </ListItemIcon>
                            <Typography variant="body2">{fname}</Typography>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No features mapped
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={4}
            textAlign="center"
          >
            {valuePack?.tiers?.length > 0 ? (
              <>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  No pricing tiers match the selected filters
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={2}>
                  Try a different duration or country, or clear the filters
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setFilterDuration("all");
                    setFilterCountry("all");
                  }}
                >
                  Clear Filters
                </Button>
              </>
            ) : (
              <>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  No pricing tiers configured
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={2}>
                  Add pricing tiers to make this value pack available for purchase
                </Typography>
                {canUpdate && (
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ color: "#fff" }}
                    onClick={() => navigate(`/value-pack/${id}/tiers`)}
                  >
                    Create Pricing Tier
                  </Button>
                )}
              </>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center">
              <IconButton
                onClick={() => navigate('/value-pack')}
                sx={{ color: '#fff', mr: 2 }}
              >
                <ArrowBackIcon />
              </IconButton>
              <MDTypography variant="h6" color="white">
                {valuePack.name} - Details
              </MDTypography>
            </Box>

            {canUpdate && (
              <Button
                variant="contained"
                color="light"
                onClick={() => navigate(`/value-pack/edit/${id}`)}
                startIcon={<EditIcon />}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.3)',
                  }
                }}
              >
                Edit
              </Button>
            )}
          </MDBox>

          <MDBox p={3}>
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{ mb: 3 }}
            >
              <Tab label="Basic Info" />
              <Tab label="Features" />
              <Tab label="Pricing Tiers" />
              {/* <Tab label="SEO & Display" /> */}
            </Tabs>

            {activeTab === 0 && renderBasicInfo()}
            {activeTab === 1 && renderFeatures()}
            {activeTab === 2 && renderPricingTiers()}
            {/* {activeTab === 3 && renderSeoDisplay()} */}
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
};

export default ValuePackView;
